<template>
  <v-container fluid class="markers-container">
    <!-- Header Section -->
    <v-row class="align-center mb-4">
      <v-col cols="12" sm="6" class="d-flex align-center py-0">
        <v-icon color="primary" size="36" class="mr-2 mr-sm-3">
          mdi-dna
        </v-icon>
        <h2 class="text-h5 text-sm-h4 font-weight-medium primary--text">
          Marcadores
        </h2>
      </v-col>

      <v-col cols="12" sm="6" class="py-0 d-flex justify-sm-end flex-wrap">
        <!-- Action Buttons Group -->
        <div class="d-flex flex-wrap">
          <v-btn
            v-if="!hideValidations"
            color="primary"
            @click="showNewMarkerModalV2 = true"
            :disabled="!hasPermission('lab_marker_write')"
            class="mr-2 mb-2"
            small
            depressed
          >
            <v-icon small left>mdi-plus</v-icon>
            <span class="hidden-xs-only">Añadir marcador</span>
          </v-btn>

          <v-btn
            v-if="!hideValidations"
            color="primary"
            @click="showNewIdentification = true"
            class="mr-2 mb-2"
            small
            depressed
          >
            <v-icon small left>mdi-bacteria</v-icon>
            <span class="hidden-xs-only">Añadir microorganismo</span>
          </v-btn>

          <!-- Filter Menu -->
          <v-menu offset-y :close-on-content-click="false" v-model="filterMenu">
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                color="primary"
                v-bind="attrs"
                v-on="on"
                class="mr-2 mb-2"
                small
                depressed
              >
                <v-icon small left>mdi-filter</v-icon>
                <span class="hidden-xs-only">Filtro</span>
              </v-btn>
            </template>
            <v-card width="300">
              <v-card-title class="text-subtitle-1 font-weight-bold">
                Filtros
              </v-card-title>
              <v-card-text>
                <v-container class="py-0">
                  <v-row>
                    <v-col cols="12" class="py-2">
                      <v-autocomplete
                        label="Tipo"
                        outlined
                        dense
                        :items="moTypes"
                        item-text="name"
                        v-model="filter.type"
                        clearable
                        hide-details
                      ></v-autocomplete>
                    </v-col>
                    <v-col cols="12" class="py-2">
                      <v-autocomplete
                        label="Simbiosis"
                        outlined
                        dense
                        :items="symbiosis"
                        item-text="name"
                        v-model="filter.symbiosis"
                        clearable
                        hide-details
                      ></v-autocomplete>
                    </v-col>
                    <v-col cols="12" class="py-2">
                      <v-autocomplete
                        label="Estatus"
                        outlined
                        dense
                        :items="statusMarker"
                        item-text="name"
                        v-model="filter.isActive"
                        clearable
                        hide-details
                      ></v-autocomplete>
                    </v-col>
                  </v-row>
                </v-container>
              </v-card-text>
              <v-card-actions class="px-4">
                <v-btn color="warning" @click="cleanFilter" small text>
                  Restaurar
                </v-btn>
                <v-spacer></v-spacer>
                <v-btn color="primary" @click="applyFilter" small depressed>
                  Aplicar
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-menu>

          <!-- Export Buttons -->
          <v-btn
            v-if="!hideValidations"
            color="primary"
            @click="exportData()"
            :disabled="selectedMarkers.length == 0"
            :loading="isExporting"
            class="mr-2 mb-2"
            small
            depressed
          >
            <v-icon small left>mdi-file-excel</v-icon>
            <span class="hidden-sm-and-down">Exportar Oligo</span>
            <v-chip v-if="selectedMarkers.length != 0" small class="ml-1">
              {{ selectedMarkers.length }}
            </v-chip>
          </v-btn>

          <v-btn
            color="primary"
            @click="exportCqData()"
            :disabled="selectedMarkers.length == 0"
            :loading="isCqExporting"
            class="mr-2 mb-2"
            small
            depressed
          >
            <v-icon small left>mdi-file-excel</v-icon>
            <span class="hidden-sm-and-down">Exportar CQ's</span>
            <v-chip v-if="selectedMarkers.length != 0" small class="ml-1">
              {{ selectedMarkers.length }}
            </v-chip>
          </v-btn>

          <!-- Action Buttons -->
          <v-btn
            v-if="!hideValidations"
            color="primary"
            @click="showValidationNotificationModal = true"
            :disabled="selectedMarkers.length == 0"
            :loading="isSendingMessage"
            small
            depressed
            class="mr-2 mb-2"
          >
            <v-icon small left>mdi-bell</v-icon>
            <span class="hidden-sm-and-down">Notificar</span>
            <v-chip v-if="selectedMarkers.length != 0" small class="ml-1">
              {{ selectedMarkers.length }}
            </v-chip>
          </v-btn>

          <v-btn
            v-if="!hideValidations"
            color="primary"
            small
            depressed
            @click="handleNewBioassays"
            :disabled="selectedMarkers.length == 0"
            :loading="isSendingMessage"
            class="mr-2 mb-2"
          >
            <v-icon small left>mdi-test-tube</v-icon>
            Bioensayo
            <v-chip v-if="selectedMarkers.length != 0" small class="ml-1">
              {{ selectedMarkers.length }}
            </v-chip>
          </v-btn>

          <v-btn
            v-if="!hideValidations"
            color="primary"
            small
            depressed
            @click="showReleasePackageModal = true"
            :disabled="selectedMarkers.length == 0"
            :loading="isSendingMessage"
            class="mb-2"
          >
            <v-icon small left>mdi-package</v-icon>
            Generar paquete
            <v-chip v-if="selectedMarkers.length != 0" small class="ml-1">
              {{ selectedMarkers.length }}
            </v-chip>
          </v-btn>
        </div>
      </v-col>
    </v-row>

    <!-- Search Section -->
    <v-row class="mb-4">
      <v-col cols="12" md="8" lg="6" class="py-0">
        <v-text-field
          outlined
          dense
          prepend-inner-icon="mdi-magnify"
          label="Buscar..."
          clearable
          v-model="search"
          background-color="white"
          hide-details
        ></v-text-field>
      </v-col>
    </v-row>

    <v-card class="elevation-2">
      <v-data-table
        v-model="selectedMarkers"
        :show-select="!hideSelectionTable"
        item-key="_id"
        :headers="filteredHeaders"
        :items="markers"
        :page.sync="page"
        :items-per-page="10"
        :loading="loadTable"
        mobile-breakpoint="0"
        hide-default-footer
        fixed-header
        :height="tableHeight"
        no-data-text="No hay marcadores disponibles"
        class="markers-table"
        :class="{ 'pa-2': $vuetify.breakpoint.smAndUp }"
      >
        <template #[`item.molecularMarkerID`]="{ item }">
          <span v-if="item.molecularMarkerID">{{
            item.molecularMarkerID
          }}</span>
          <span v-else>--------</span>
        </template>

        <template #[`item.microorganism_meta.name`]="{ item }">
          <v-chip
            :color="item.isActive === false ? 'red' : 'primary'"
            small
            :dark="item.isActive === false"
          >
            {{ getMarkerName(item) }}
          </v-chip>
        </template>

        <template #[`item.updatedAt`]="{ item }">
          <span>{{ dateFormat(item.updatedAt) }}</span>
        </template>

        <template #[`item.createdAt`]="{ item }">
          <span>{{ dateFormat(item.createdAt) }}</span>
        </template>

        <template #[`item.microorganism_meta.moType`]="{ item }">
          <div>
            <span v-if="item.markerType === 'MO'">
              {{
                getMOType(
                  item.microorganism_meta
                    ? item.microorganism_meta.moType
                    : "functional"
                )
              }}
              <span v-if="item.isPathogen">(Patógeno)</span>
              <span v-else>(Benéfico)</span>
            </span>
            <span v-else-if="item.markerType === 'FNC'">Funcional</span>
            <span v-else-if="item.markerType === 'MO_FNC'"
              >Funcional/Taxonómico</span
            >
            <div v-if="item.fluorophore" class="primary--text text-caption">
              {{ item.fluorophore }}
            </div>
          </div>
        </template>

        <template #[`item.cqMax`]="{ item }">
          <div class="d-flex flex-column">
            <span>min: {{ parseFloat(item.cqMin).toFixed(2) }}</span>
            <span>max: {{ parseFloat(item.cqMax).toFixed(2) }}</span>
          </div>
        </template>

        <template #[`item.cqByLab`]="{ item }">
          <div class="d-flex align-center cq-container">
            <v-menu
              offset-y
              max-height="500px"
              height="500px"
              max-width="350px"
              :close-on-content-click="false"
              v-model="item.mainMenu"
            >
              <template v-slot:activator="{ on: menuOn, attrs: menuAttrs }">
                <v-btn
                  small
                  text
                  color="primary"
                  v-bind="menuAttrs"
                  v-on="menuOn"
                  class="px-1"
                >
                  <v-icon small left>mdi-flask</v-icon>
                  {{ (item.cqByLab && item.cqByLab.length) || 0 }} CQ's
                  <v-icon right small>mdi-chevron-down</v-icon>
                </v-btn>
              </template>

              <v-card>
                <v-card-title
                  class="text-subtitle-1 d-flex justify-space-between align-center"
                >
                  <span>Valores CQ por Laboratorio</span>

                  <v-menu
                    v-model="item.labMenu"
                    offset-x
                    :close-on-content-click="false"
                    max-height="500px"
                    max-width="350px"
                  >
                    <template v-slot:activator="{ on: onActionsMenuLab }">
                      <v-btn
                        icon
                        small
                        color="primary"
                        v-on="onActionsMenuLab"
                        @click.stop="handleOpenLabMenu(item)"
                        :disabled="!hasPermission('lab_management_cq')"
                      >
                        <v-icon small>mdi-plus</v-icon>
                      </v-btn>
                    </template>
                    <v-card>
                      <v-card-title
                        class="text-subtitle-1 d-flex justify-space-between align-center"
                      >
                        <span>Añadir Laboratorio</span>
                        <v-btn icon small @click="item.labMenu = false">
                          <v-icon>mdi-close</v-icon>
                        </v-btn>
                      </v-card-title>

                      <v-card-text>
                        <v-autocomplete
                          v-model="selectedLabToAdd"
                          :items="laboratories"
                          item-text="name"
                          item-value="_id"
                          label="Seleccionar laboratorio"
                          outlined
                          dense
                          clearable
                          class="mb-3"
                          return-object
                        ></v-autocomplete>

                        <v-text-field
                          v-model.number="newCqValues.cqMin"
                          label="CQ Mínimo"
                          type="number"
                          outlined
                          dense
                          step="0.1"
                          class="mb-2"
                        ></v-text-field>

                        <v-text-field
                          v-model.number="newCqValues.cqMax"
                          label="CQ Máximo"
                          type="number"
                          outlined
                          dense
                          step="0.1"
                        ></v-text-field>

                        <div class="d-flex justify-end mt-4">
                          <v-btn
                            small
                            color="primary"
                            @click="addLabToMarker(item)"
                            :loading="item.isAddingLab"
                          >
                            <v-icon left>mdi-check</v-icon>
                            Añadir
                          </v-btn>
                        </div>
                      </v-card-text>
                    </v-card>
                  </v-menu>
                </v-card-title>

                <v-card-text class="pa-0">
                  <v-list dense>
                    <template v-if="item.cqByLab && item.cqByLab.length > 0">
                      <v-list-item
                        v-for="(cqItem, index) in item.cqByLab"
                        :key="`${item._id}-${index}`"
                        class="px-2"
                      >
                        <v-list-item-content>
                          <div
                            class="d-flex justify-space-between align-center"
                          >
                            <div>
                              <v-list-item-title class="font-weight-medium">
                                {{ getLabName(cqItem.laboratoryReference) }}
                              </v-list-item-title>
                              <v-list-item-subtitle
                                class="d-flex justify-space-between mt-1"
                              >
                                <span class="mr-2"
                                  >Max:
                                  {{
                                    cqItem.cqMax
                                      ? parseFloat(cqItem.cqMax).toFixed(2)
                                      : "0.00"
                                  }}</span
                                >
                                <span
                                  >Min:
                                  {{
                                    cqItem.cqMin
                                      ? parseFloat(cqItem.cqMin).toFixed(2)
                                      : "0.00"
                                  }}</span
                                >
                              </v-list-item-subtitle>
                            </div>

                            <v-menu
                              offset-x
                              :close-on-content-click="false"
                              v-model="cqItem.cqMenu"
                            >
                              <template
                                v-slot:activator="{ on: onActionsMenu }"
                              >
                                <v-btn
                                  icon
                                  x-small
                                  v-on="onActionsMenu"
                                  color="primary"
                                  @click="handleOpenCqMenu(cqItem)"
                                  :disabled="
                                    !hasPermission('lab_management_cq')
                                  "
                                >
                                  <v-icon x-small>mdi-dots-vertical</v-icon>
                                </v-btn>
                              </template>

                              <v-card width="250">
                                <v-card-text>
                                  <v-text-field
                                    label="CQ Máximo"
                                    type="number"
                                    outlined
                                    dense
                                    step="0.1"
                                    v-model="newCqValues.cqMax"
                                    :rules="[
                                      (v) =>
                                        v >= 0 ||
                                        'El valor debe ser mayor o igual a 0',
                                      (v) =>
                                        v >= newCqValues.cqMin ||
                                        'El CQ máximo debe ser mayor o igual al mínimo',
                                    ]"
                                  ></v-text-field>

                                  <v-text-field
                                    label="CQ Mínimo"
                                    type="number"
                                    outlined
                                    dense
                                    step="0.1"
                                    class="mb-2"
                                    v-model="newCqValues.cqMin"
                                    :rules="[
                                      (v) =>
                                        v >= 0 ||
                                        'El valor debe ser mayor o igual a 0',
                                      (v) =>
                                        v <= newCqValues.cqMax ||
                                        'El CQ mínimo debe ser menor o igual al máximo',
                                    ]"
                                  ></v-text-field>

                                  <div class="d-flex justify-end">
                                    <!-- <v-btn
                                      small
                                      color="error"
                                      text
                                      @click="removeLabFromMarker(item, index)"
                                      class="mr-2"
                                    >
                                      Eliminar
                                    </v-btn> -->
                                    <v-btn
                                      small
                                      color="primary"
                                      :loading="item.isUpdatingCq"
                                      @click="updateLabCqValues(item, cqItem)"
                                    >
                                      Guardar
                                    </v-btn>
                                  </div>
                                </v-card-text>
                              </v-card>
                            </v-menu>
                          </div>
                        </v-list-item-content>
                      </v-list-item>
                    </template>

                    <v-list-item v-else>
                      <v-list-item-title class="text-caption grey--text">
                        No hay datos de CQ registrados
                      </v-list-item-title>
                    </v-list-item>
                  </v-list>
                </v-card-text>
              </v-card>
            </v-menu>
          </div>
        </template>

        <template #[`item._id`]="{ item }">
          <v-menu offset-y left>
            <template v-slot:activator="{ on, attrs }">
              <v-btn icon small v-bind="attrs" v-on="on">
                <v-icon small>mdi-dots-vertical</v-icon>
              </v-btn>
            </template>
            <v-list dense>
              <v-list-item
                @click="openUpdateForm(item)"
                v-if="!hideValidations && hasPermission('lab_marker_write')"
              >
                <v-list-item-icon class="mr-2">
                  <v-icon small>mdi-pencil</v-icon>
                </v-list-item-icon>
                <v-list-item-title>Actualizar</v-list-item-title>
              </v-list-item>

              <v-list-item
                @click="openOligoForm(item)"
                v-if="hasPermission('lab_oligo_read')"
              >
                <v-list-item-icon class="mr-2">
                  <v-icon small>mdi-dna</v-icon>
                </v-list-item-icon>
                <v-list-item-title>Oligo</v-list-item-title>
              </v-list-item>

              <v-list-item
                @click="handleUpdateFluorophore(item)"
                v-if="
                  !hideValidations && hasPermission('lab_fluorophore_write')
                "
              >
                <v-list-item-icon class="mr-2">
                  <v-icon small>mdi-palette</v-icon>
                </v-list-item-icon>
                <v-list-item-title>Fluoróforo</v-list-item-title>
              </v-list-item>

              <v-list-item
                @click="setRestoreModal(item)"
                v-if="
                  item.isActive == false &&
                  !hideValidations &&
                  hasPermission('lab_marker_write')
                "
              >
                <v-list-item-icon class="mr-2">
                  <v-icon small>mdi-restore</v-icon>
                </v-list-item-icon>
                <v-list-item-title>Restaurar</v-list-item-title>
              </v-list-item>

              <v-list-item
                @click="setDeleteModal(item)"
                v-if="
                  item.isActive == true &&
                  !hideValidations &&
                  hasPermission('lab_marker_write')
                "
              >
                <v-list-item-icon class="mr-2">
                  <v-icon small>mdi-delete</v-icon>
                </v-list-item-icon>
                <v-list-item-title>Eliminar</v-list-item-title>
              </v-list-item>

              <v-list-item @click="setTimeLine(item)">
                <v-list-item-icon class="mr-2">
                  <v-icon small>mdi-history</v-icon>
                </v-list-item-icon>
                <v-list-item-title>Historial</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </template>

        <template #[`item.oligoStatus`]="{ item }">
          <v-chip :color="getStatusColor(item.oligoStatus)" small dark>
            {{ getOligoStatus(item.oligoStatus) }}
          </v-chip>
        </template>

        <template #[`item.synthesisStatus`]="{ item }">
          <v-chip :color="getStatusColor(item.synthesisStatus)" small dark>
            {{ getSynthesisStatus(item.synthesisStatus) }}
          </v-chip>
        </template>

        <template #[`item.validationStatus`]="{ item }">
          <v-chip :color="getStatusColor(item.validationStatus)" small dark>
            {{ getValidationStatus(item.validationStatus) }}
          </v-chip>
        </template>
      </v-data-table>

      <v-pagination
        v-model="page"
        :length="numPages"
        :total-visible="paginationVisible"
        color="primary"
        class="mt-4"
      ></v-pagination>
    </v-card>
    <TimeLineMarker v-model="showTimeLine" v-if="showTimeLine" />
    <DeleteCofirmationModal
      v-model="showDeleteCofirmationModal"
      v-if="showDeleteCofirmationModal"
      @confirmation="removeMarker()"
      :title="genericModalTitle"
    />
    <RestoreCofirmationModal
      v-model="showRestoreCofirmationModal"
      v-if="showRestoreCofirmationModal"
      @confirmation="activeMarker()"
      :title="genericModalTitle"
    />
    <AddNewValidationFormatModal
      v-model="showAddNewValidationFormatModal"
      v-if="showAddNewValidationFormatModal"
      @update="(showAddNewValidationFormatModal = false), fetchData()"
    />
    <NewMarkerModalV2
      v-model="showNewMarkerModalV2"
      @close_update="fetchData(), (showNewMarkerModalV2 = false)"
      v-if="showNewMarkerModalV2"
    />
    <UpdateMarkerModal
      v-model="showUpdateMarkerModal"
      @close_update="fetchData(), (showUpdateMarkerModal = false)"
      v-if="showUpdateMarkerModal"
    />
    <ValidationNotificationModal
      v-model="showValidationNotificationModal"
      :markers="selectedMarkers"
      @email_send="handleEmailSuccess"
    />
    <AddFluorophoreModal
      v-model="showAddFluorophoreModal"
      v-if="showAddFluorophoreModal"
      @confirmation="updateFluorophoreRequest"
    />
    <NewBioassaysModal
      v-model="showNewBioassaysModal"
      :markers="selectedMarkers"
      @close_and_clean="handleCloseBioassayModal"
      v-if="showNewBioassaysModal"
    />

    <new-identification
      v-model="showNewIdentification"
      v-if="showNewIdentification"
    />
    <release-package-modal
      v-model="showReleasePackageModal"
      :markersSelected="selectedMarkers"
      @close_and_clean="handleCloseReleasePackageModal"
      v-if="showReleasePackageModal"
    />
  </v-container>
</template>

<script>
import { mapActions, mapMutations, mapState } from "vuex";
import dayjs from "dayjs";
import xlsx from "json-as-xlsx";

//components
import TimeLineMarker from "@/components/cmda_admin/modals/TimeLineMarker.vue";
import DeleteCofirmationModal from "@/components/utils/GenericConfirmationModal.vue";
import RestoreCofirmationModal from "@/components/utils/GenericConfirmationModal.vue";
import AddNewValidationFormatModal from "@/components/validations/modals/AddNewValidationFormatModal.vue";
import NewMarkerModalV2 from "../validations/modals/NewMarkerModalV2.vue";
import UpdateMarkerModal from "../validations/modals/UpdateMarkerModal.vue";
import ValidationNotificationModal from "../validations/modals/ValidationNotificationModal.vue";
import AddFluorophoreModal from "../validations/modals/AddFluorophoreModal.vue";
import NewBioassaysModal from "./modals/NewBioassaysModal.vue";
import NewIdentification from "./modals/NewIdentification.vue";
import ReleasePackageModal from "./modals/ReleasePackageModal.vue";

export default {
  props: {
    showAddNewFormattValidations: {
      type: Boolean,
      default: false,
    },
    hideValidations: {
      type: Boolean,
      default: true,
    },
    hideSelectionTable: {
      type: Boolean,
      default: true,
    },
  },
  components: {
    TimeLineMarker,
    DeleteCofirmationModal,
    RestoreCofirmationModal,
    AddNewValidationFormatModal,
    NewMarkerModalV2,
    UpdateMarkerModal,
    ValidationNotificationModal,
    AddFluorophoreModal,
    NewBioassaysModal,
    NewIdentification,
    ReleasePackageModal,
  },
  data: () => ({
    headers: [
      {
        text: "ID",
        sortable: false,
        value: "molecularMarkerID",
      },
      {
        text: "Oligo",
        sortable: false,
        value: "oligoStatus",
      },
      {
        text: "Síntesis",
        sortable: false,
        value: "synthesisStatus",
      },
      {
        text: "Validación",
        sortable: false,
        value: "validationStatus",
      },
      {
        text: "Versión",
        value: "version",
        sortable: false,
        align: "center",
      },
      {
        text: "Nombre",
        value: "microorganism_meta.name",
        sortable: false,
      },
      {
        text: "Cq's",
        value: "cqByLab",
        sortable: false,
      },
      {
        text: "Tipo",
        value: "microorganism_meta.moType",
        sortable: false,
      },
      {
        text: "Fecha creación",
        value: "createdAt",
        sortable: false,
      },
      {
        text: "Ultima actualización",
        value: "updatedAt",
        sortable: false,
      },
      {
        text: "Acciones",
        value: "_id",
        sortable: false,
      },
    ],
    markers: [],
    per_page: 10,
    page: 1,
    numPages: 1,
    sort: {
      createdAt: -1,
    },
    search: null,
    filter: {
      symbiosis: null,
      type: null,
      isActive: null,
    },
    moTypes: [
      {
        name: "Bacteria",
        value: "bacteria",
      },
      {
        name: "Hongo",
        value: "fungi",
      },
      {
        name: "Nematodo",
        value: "nematode",
      },
    ],
    symbiosis: [
      {
        name: "Benéfico",
        value: false,
      },
      {
        name: "Patógeno",
        value: true,
      },
    ],
    statusMarker: [
      {
        name: "Activo",
        value: "active",
      },
      {
        name: "Suspendido",
        value: "suspended",
      },
    ],
    loadTable: false,
    showFlowComponent: {
      flag: false,
      component: "",
    },
    filterMenu: false,
    genericModalTitle: "",
    selectedMarkers: [],
    newCqValues: { cqMin: 0, cqMax: 0 },
    selectedLabToAdd: null,
    // loaders
    isExporting: false,
    isCqExporting: false,
    isSendingMessage: false,
    // modals
    showTimeLine: false,
    showDeleteCofirmationModal: false,
    showRestoreCofirmationModal: false,
    showAddNewValidationFormatModal: false,
    showNewMarkerModalV2: false,
    showUpdateMarkerModal: false,
    showValidationNotificationModal: false,
    showAddFluorophoreModal: false,
    showNewBioassaysModal: false,
    showNewIdentification: false,
    showReleasePackageModal: false,
  }),
  computed: {
    ...mapState("laboratory", ["molecularMarkerToUpdate", "laboratories"]),
    ...mapState("molecularMarker", ["markerSelected"]),
    ...mapState("session", ["permissions"]),

    filteredHeaders() {
      if (this.$vuetify.breakpoint.xs) {
        return this.headers.filter((h) =>
          ["molecularMarkerID", "microorganism_meta.name", "_id"].includes(
            h.value
          )
        );
      } else if (this.$vuetify.breakpoint.sm) {
        return this.headers.filter((h) =>
          [
            "molecularMarkerID",
            "microorganism_meta.name",
            "oligoStatus",
            "_id",
          ].includes(h.value)
        );
      }
      return this.headers;
    },

    tableHeight() {
      if (this.$vuetify.breakpoint.xs) return "400px";
      if (this.$vuetify.breakpoint.sm) return "500px";
      return "56vh";
    },

    paginationVisible() {
      if (this.$vuetify.breakpoint.xs) return 5;
      if (this.$vuetify.breakpoint.sm) return 7;
      return 10;
    },
  },
  methods: {
    ...mapActions("laboratory", [
      "getMarkers",
      "deleteMarker",
      "restoreMarker",
      "getLaboratories",
    ]),
    ...mapMutations("laboratory", ["SET_SELECTED_MOLECULAR_MARKER_TO_UPDATE"]),
    ...mapMutations("alerts", ["SET_ALERT_TYPE"]),
    ...mapMutations("molecularMarker", ["SET_MARKER_SELECTED"]),
    ...mapActions("molecularMarker", [
      "getLastOligoFromMarkerList",
      "updateFluorophore",
      "addNewLaboratoryRange",
      "updateCqRanges",
    ]),

    getMarkerName(item) {
      if (item.microorganism_meta?.name) {
        return item.microorganism_meta.name;
      } else if (item.soilFunction?.es) {
        return item.soilFunction.es;
      }
      return "Nombre no disponible";
    },

    getMOType(type) {
      if (!type) return "Tipo no disponible";

      const itemsMoType = {
        all: "Todos",
        bacteria: "Bacteria",
        fungi: "Hongo",
        nematode: "Nemátodo",
        functional: "Funcional",
      };
      return itemsMoType[type] || type;
    },

    getStatusColor(status) {
      const statusColors = {
        NOT_STARTED: "primary",
        PENDING: "orange",
        DESIGNING: "blue",
        IN_PROGRESS: "orange",
        FINISHED: "secondary",
        DISCARDED: "red",
      };
      return statusColors[status] || "primary";
    },

    hasPermission(permission) {
      return this.permissions?.includes(permission);
    },

    async fetchData() {
      try {
        this.loadTable = true;
        let payload = {
          filter: this.filter,
          per_page: this.per_page,
          page: this.page,
          sort: this.sort,
        };
        let response = await this.getMarkers(payload);
        if (response.error) throw Error(response.message);
        this.numPages = response.pages;
        this.markers = response.molecularMarkers.map((marker) => {
          return {
            ...marker,
            mainMenu: false,
            cqMenu: false,
            labMenu: false,
            isAddingLab: false,
            isUpdatingCq: false,
          };
        });
      } catch (error) {
        console.log(error);
        this.SET_ALERT_TYPE({
          visible: true,
          type: "error",
          text: `Algo salió mal. Error: ${error.message}`,
          timeout: 5000,
        });
      }
      this.loadTable = false;
    },

    dateFormat(value) {
      const date = `${dayjs(value).format("DD/MM/YYYY - HH:mm")} hrs`;
      return date || "--/--/----";
    },

    async fetchAndClear() {
      this.showFlowComponent = {
        flag: false,
        component: "",
      };
      this.SET_SELECTED_MOLECULAR_MARKER_TO_UPDATE(null);
      await this.fetchData();
    },

    async cleanFilter() {
      this.filterMenu = false;
      this.filter = {
        symbiosis: null,
        type: null,
        isActive: null,
      };
      await this.fetchData();
    },

    async applyFilter() {
      this.page = 1;
      this.numPages = 1;
      this.filterMenu = false;
      await this.fetchData();
    },

    openUpdateForm(item) {
      this.SET_MARKER_SELECTED(item);
      this.showUpdateMarkerModal = true;
    },

    openOligoForm(item) {
      this.SET_MARKER_SELECTED(item);
      this.$emit("openOligo");
    },

    setTimeLine(item) {
      this.SET_SELECTED_MOLECULAR_MARKER_TO_UPDATE(item);
      this.showTimeLine = true;
    },

    setDeleteModal(item) {
      this.SET_SELECTED_MOLECULAR_MARKER_TO_UPDATE(item);
      this.genericModalTitle = `¿Estas seguro que quiere eliminar el marcador ${this.getMarkerName(
        item
      )} (${item.molecularMarkerID})?`;
      this.showDeleteCofirmationModal = true;
    },

    setRestoreModal(item) {
      this.SET_SELECTED_MOLECULAR_MARKER_TO_UPDATE(item);
      this.genericModalTitle = `¿Estas seguro que quiere restaurar el marcador ${this.getMarkerName(
        item
      )} (${item.molecularMarkerID})?`;
      this.showRestoreCofirmationModal = true;
    },

    async removeMarker() {
      try {
        this.molecularMarkerToUpdate.isDeleting = true;
        let payload = {
          markerId: this.molecularMarkerToUpdate._id,
        };
        await this.deleteMarker(payload);
        this.SET_ALERT_TYPE({
          visible: true,
          type: "success",
          text: `Marcador eliminado con exito`,
          timeout: 5000,
        });

        this.page = 1;
        await this.fetchData();
      } catch (error) {
        console.log(error);
        this.SET_ALERT_TYPE({
          visible: true,
          type: "error",
          text: `Algo salió mal. Error: ${error.message}`,
          timeout: 5000,
        });
      }
    },

    async activeMarker() {
      try {
        this.molecularMarkerToUpdate.isDeleting = true;
        let payload = {
          markerId: this.molecularMarkerToUpdate._id,
        };
        await this.restoreMarker(payload);
        this.SET_ALERT_TYPE({
          visible: true,
          type: "success",
          text: `Marcador restaurado con exito`,
          timeout: 5000,
        });

        this.page = 1;
        await this.fetchData();
      } catch (error) {
        console.log(error);
        this.SET_ALERT_TYPE({
          visible: true,
          type: "error",
          text: `Algo salió mal. Error: ${error.message}`,
          timeout: 5000,
        });
      }
    },

    getOligoStatus(value) {
      let status = {
        NOT_STARTED: "Sin iniciar",
        PENDING: "Pendiente",
        DESIGNING: "Diseño",
        FINISHED: "Terminado",
        DISCARDED: "Descartado",
      };
      return status[value] || value;
    },

    getSynthesisStatus(value) {
      let status = {
        NOT_STARTED: "Sin iniciar",
        IN_PROGRESS: "En progreso",
        FINISHED: "Terminado",
      };
      return status[value] || value;
    },

    getValidationStatus(value) {
      let status = {
        NOT_STARTED: "Sin iniciar",
        IN_PROGRESS: "En progreso",
        FINISHED: "Terminado",
      };
      return status[value] || value;
    },

    async exportData() {
      try {
        this.isExporting = true;
        let exportHeaders1 = [
          {
            label: "Name",
            value: "name",
          },
          {
            label: "Sequence",
            value: "sequence",
          },
          {
            label: "Scale",
            value: "scale",
          },
          {
            label: "Purification",
            value: "purification",
          },
          {
            label: "Oligo",
            value: "oligoId",
          },
        ];
        let exportHeaders2 = [
          {
            label: "Name",
            value: "name",
          },
          {
            label: "Sequence",
            value: "sequence",
          },
          {
            label: "Oligo",
            value: "oligoId",
          },
        ];
        let probe = [];
        let forwarAndReverse = [];

        let payload = {
          markers: this.selectedMarkers.map((marker) => {
            return marker._id;
          }),
        };

        let response = await this.getLastOligoFromMarkerList(payload);
        probe = response.probe;
        forwarAndReverse = response.forwarAndReverse;

        const actually_date = new Date();
        let fileTitle1 = `Primers Forward and Reverse - ${actually_date
          .toLocaleDateString()
          .toString()
          .replaceAll("/", "-")}`;

        let fileTitle2 = `Sonda - ${actually_date
          .toLocaleDateString()
          .toString()
          .replaceAll("/", "-")}`;
        this.exportJsonToXlsx(exportHeaders1, forwarAndReverse, fileTitle1);
        this.exportJsonToXlsx(exportHeaders2, probe, fileTitle2);
        this.SET_ALERT_TYPE({
          visible: true,
          type: "success",
          text: "Información exportada",
          timeout: 4000,
        });
      } catch (error) {
        console.log(error);
        this.SET_ALERT_TYPE({
          visible: true,
          type: "error",
          text: `Algo salió mal. Error: ${error.message}`,
          timeout: 5000,
        });
      }
      this.isExporting = false;
    },

    exportJsonToXlsx(columns, content, name) {
      const settings = {
        fileName: name,
      };
      xlsx(
        [
          {
            sheet: "Hoja 1",
            columns,
            content,
          },
        ],
        settings
      );
    },

    async exportCqData() {
      try {
        this.isCqExporting = true;

        let exportHeaders = [
          { label: "Marker ID", value: "molecularMarkerID" },
          { label: "Nombre", value: "name" },
          { label: "Tipo de Marcador", value: "markerType" },
          { label: "Laboratorio", value: "labName" },
          { label: "CQ Mínimo (Lab)", value: "labCqMin" },
          { label: "CQ Máximo (Lab)", value: "labCqMax" },
          // { label: "CQ Mínimo Global", value: "cqMin" },
          // { label: "CQ Máximo Global", value: "cqMax" },
          { label: "Es Patógeno", value: "isPathogen" },
          { label: "Versión", value: "version" },
          { label: "Estado", value: "status" },
          // { label: "Proyecto", value: "project" },
          { label: "Fluoróforo", value: "fluorophore" },
          // { label: "Paquete", value: "packageName" }
        ];

        // Transformar los datos para la exportación
        let data = [];

        this.selectedMarkers.forEach((marker) => {
          const name =
            marker.markerType === "MO"
              ? marker.microorganism_meta?.name
              : marker.soilFunction?.es || "";

          if (!marker.cqByLab || marker.cqByLab.length === 0) {
            data.push({
              molecularMarkerID: marker.molecularMarkerID,
              name: name,
              markerType:
                marker.markerType === "MO" ? "Microorganismo" : "Funcional",
              labName: "N/A",
              labCqMin: "N/A",
              labCqMax: "N/A",
              cqMin: marker.cqMin,
              cqMax: marker.cqMax,
              isPathogen: marker.isPathogen ? "Sí" : "No",
              version: marker.version,
              status: marker.isActive ? "Activo" : "Inactivo",

              fluorophore: marker.fluorophore || "N/A",
            });
          } else {
            marker.cqByLab.forEach((lab) => {
              data.push({
                molecularMarkerID: marker.molecularMarkerID,
                name: name,
                markerType:
                  marker.markerType === "MO" ? "Microorganismo" : "Funcional",
                labName:
                  lab.laboratoryReference?.name || "Laboratorio desconocido",
                labCqMin: lab.cqMin,
                labCqMax: lab.cqMax,
                cqMin: marker.cqMin,
                cqMax: marker.cqMax,
                isPathogen: marker.isPathogen ? "Sí" : "No",
                version: marker.version,
                status: marker.isActive ? "Activo" : "Inactivo",

                fluorophore: marker.fluorophore || "N/A",
              });
            });
          }
        });

        const actually_date = new Date();
        let fileTitle = `Exportacion_Marcadores_con_Laboratorios_${actually_date
          .toLocaleDateString()
          .toString()
          .replaceAll("/", "-")}`;

        this.exportJsonToXlsx(exportHeaders, data, fileTitle);

        this.SET_ALERT_TYPE({
          visible: true,
          type: "success",
          text: "Información exportada correctamente con datos de laboratorios",
          timeout: 4000,
        });
      } catch (error) {
        console.log(error);
        this.SET_ALERT_TYPE({
          visible: true,
          type: "error",
          text: `Error al exportar: ${error.message}`,
          timeout: 5000,
        });
      } finally {
        this.isCqExporting = false;
      }
    },

    async handleEmailSuccess() {
      this.showValidationNotificationModal = false;
      this.selectedMarkers = [];
      await this.fetchData();
    },

    handleUpdateFluorophore(item) {
      this.SET_MARKER_SELECTED(item);
      this.showAddFluorophoreModal = true;
    },

    async updateFluorophoreRequest(fluorophore) {
      try {
        let params = {
          markerId: this.markerSelected._id,
          fluorophore: fluorophore,
        };
        await this.updateFluorophore({
          params: params,
          body: {},
        });
        this.SET_ALERT_TYPE({
          visible: true,
          type: "success",
          text: `Fluoróforo actualizado`,
          timeout: 5000,
        });
        await this.fetchData();
      } catch (error) {
        console.log(error);
        this.SET_ALERT_TYPE({
          visible: true,
          type: "error",
          text: `Algo salió mal. Error: ${error.message}`,
          timeout: 5000,
        });
      }
    },
    handleNewBioassays() {
      let hasProject = false;
      for (const marker of this.selectedMarkers) {
        if (marker.project && typeof marker.project == "object") {
          hasProject = true;
          break;
        }
      }
      if (!hasProject) {
        this.SET_ALERT_TYPE({
          visible: true,
          type: "info",
          text: `Ningún marcador seleccionado tiene asociado algún proyecto`,
          timeout: 5000,
        });
      } else {
        this.showNewBioassaysModal = true;
      }
    },
    handleCloseBioassayModal() {
      this.selectedMarkers = [];
      this.showNewBioassaysModal = false;
    },
    getLabName(labRef) {
      if (typeof labRef === "object" && labRef) {
        return labRef.name || "Laboratorio";
      }
      return "Laboratorio " + (labRef ? labRef.substring(0, 4) : "");
    },
    handleOpenCqMenu(item) {
      this.newCqValues.cqMin = item.cqMin || 0;
      this.newCqValues.cqMax = item.cqMax || 0;
    },
    handleOpenLabMenu(marker) {
      this.newCqValues.cqMin = 0;
      this.newCqValues.cqMax = 0;
      this.selectedLabToAdd = null;
      marker.labMenu = true;
    },
    async addLabToMarker(marker) {
      try {
        marker.isAddingLab = true;
        let payload = {
          markerId: marker._id,
          laboratoryId: this.selectedLabToAdd._id,
          cqMin: this.newCqValues.cqMin,
          cqMax: this.newCqValues.cqMax,
        };
        let response = await this.addNewLaboratoryRange({
          params: payload,
          body: {},
        });
        this.SET_ALERT_TYPE({
          visible: true,
          type: "success",
          text: `Laboratorio añadido correctamente`,
          timeout: 5000,
        });
        // Add the log to the marker's logs
        marker.logs.push(response.log);
        // Add the new lab to the marker's cqByLab
        marker.cqByLab.push({
          laboratoryReference: this.selectedLabToAdd,
          cqMin: this.newCqValues.cqMin,
          cqMax: this.newCqValues.cqMax,
          cqMenu: false,
        });
        this.newCqValues = { cqMin: 0, cqMax: 0 };
        this.selectedLabToAdd = null;
        marker.labMenu = false;
      } catch (error) {
        console.error("Error adding lab:", error);
        this.SET_ALERT_TYPE({
          visible: true,
          type: "error",
          text: `Algo salió mal. Error: ${error.message}`,
          timeout: 5000,
        });
      } finally {
        marker.isAddingLab = false;
      }
    },

    async updateLabCqValues(marker, lab) {
      try {
        marker.isUpdatingCq = true;
        let payload = {
          markerId: marker._id,
          laboratoryId: lab.laboratoryReference._id,
          cqMin: this.newCqValues.cqMin,
          cqMax: this.newCqValues.cqMax,
        };
        let response = await this.updateCqRanges({
          params: payload,
          body: {},
        });
        this.SET_ALERT_TYPE({
          visible: true,
          type: "success",
          text: `Valores CQ actualizados correctamente`,
          timeout: 5000,
        });
        // Add the log to the marker's logs
        marker.logs.push(response.log);
        // Update the marker's cqByLab with the new values
        marker.cqByLab = marker.cqByLab.map((cqItem) => {
          if (cqItem.laboratoryReference === lab.laboratoryReference) {
            return {
              ...cqItem,
              cqMin: this.newCqValues.cqMin,
              cqMax: this.newCqValues.cqMax,
              cqMenu: false,
            };
          }
          return cqItem;
        });
        this.newCqValues = { cqMin: 0, cqMax: 0 };
        this.selectedLabToAdd = null;
      } catch (error) {
        console.error("Error updating CQ values:", error);
        this.SET_ALERT_TYPE({
          visible: true,
          type: "error",
          text: `Algo salió mal. Error: ${error.message}`,
          timeout: 5000,
        });
      } finally {
        marker.isUpdatingCq = false;
      }
    },
    handleCloseReleasePackageModal() {
      this.selectedMarkers = [];
      this.showReleasePackageModal = false;
    },
  },
  async mounted() {
    await this.getLaboratories({ searchCMDA: 1 });
    await this.fetchData();
  },
  watch: {
    async page() {
      await this.fetchData();
    },

    async search(value) {
      this.page = 1;
      this.numPages = 1;
      if (value) {
        this.filter = {
          recommendationType: {
            $exists: false,
          },
          search: value,
        };
      } else {
        this.filter = {
          recommendationType: {
            $exists: false,
          },
        };
      }

      await this.fetchData();
    },
  },
};
</script>

<style scoped>
.markers-container {
  padding: 12px;
}

.markers-table {
  border-radius: 8px;
}

@media (max-width: 600px) {
  .markers-container {
    padding: 8px;
  }

  .v-data-table ::v-deep .v-data-table__mobile-row {
    min-height: 40px;
    padding: 8px 16px;
  }

  .v-data-table ::v-deep .v-data-table__mobile-row__header {
    font-weight: 600;
    color: var(--v-primary-base);
  }
}

@media (max-width: 960px) {
  .v-data-table ::v-deep th,
  .v-data-table ::v-deep td {
    padding: 0 8px;
  }
}

.v-chip {
  margin: 2px 0;
}

.cq-cell {
  min-width: 120px;
}

.cq-menu-item {
  border-bottom: 1px solid #eee;
  padding: 8px 0;
}

.cq-values {
  display: flex;
  justify-content: space-between;
  width: 100%;
}

@media (max-width: 600px) {
  .cq-cell {
    min-width: auto;
  }
}
</style>
